/**
 * Telegraph / Graph Messager-style bot account support.
 * Bots cannot use messages.getHistory — load known message IDs via messages.getMessages instead.
 */
import type { ApiChat, ApiMessage } from '../api/types';
import type { ThreadId } from '../types';
import type { GlobalState } from '../global/types';
import { MAIN_THREAD_ID } from '../api/types';
import { LoadMoreDirection } from '../types';
import { MESSAGE_LIST_SLICE } from '../config';
import { getMessageKey, isLocalMessageId } from '../util/keys/messageKey';
import { buildCollectionByKey, unique } from '../util/iteratees';
import { getUserFullName, hasMessageText, orderHistoryIds } from '../global/helpers';
import { getGlobal, setGlobal } from '../global';
import {
  addChatMessagesById,
  addUserStatuses,
  safeReplaceViewportIds,
  updateChats,
  updateListedIds,
  updateUsers,
} from '../global/reducers';
import {
  selectChat,
  selectChatLastMessageId,
  selectChatMessage,
  selectChatMessages,
  selectFocusedMessageId,
  selectListedIds,
  selectRealLastReadId,
  selectUser,
  selectViewportIds,
} from '../global/selectors';
import { selectThreadIdFromMessage } from '../global/selectors/threads';
import { callApi } from '../api/gramjs';

export const BOT_KNOWN_HISTORY_PAGE_SIZE = 20;
export const BOT_KNOWN_HISTORY_MAX_PAGE_REQUESTS = 5;
export const BOT_KNOWN_HISTORY_TARGET_SIZE = MESSAGE_LIST_SLICE / 2;

const botKnownHistoryRequestKeys = new Set<string>();
const botKnownHistoryEmptyPageKeys = new Set<string>();
const botViewportRepairAttemptKeys = new Set<string>();
const messageRequestKeys = new Set<string>();

export function clearBotMessagerRequestCache() {
  botKnownHistoryRequestKeys.clear();
  botKnownHistoryEmptyPageKeys.clear();
  botViewportRepairAttemptKeys.clear();
  messageRequestKeys.clear();
}

function findClosestIndex(sourceIds: number[], offsetId: number) {
  if (offsetId < sourceIds[0]) {
    return 0;
  }

  if (offsetId > sourceIds[sourceIds.length - 1]) {
    return sourceIds.length - 1;
  }

  return sourceIds.findIndex((id, i) => (
    id === offsetId
    || (id < offsetId && sourceIds[i + 1] > offsetId)
  ));
}

function getViewportSlice(
  sourceIds: number[],
  offsetId: number | undefined,
  direction: LoadMoreDirection,
) {
  const { length } = sourceIds;
  const index = offsetId ? findClosestIndex(sourceIds, offsetId) : -1;
  const isBackwards = direction === LoadMoreDirection.Backwards;
  const isAround = direction === LoadMoreDirection.Around;
  const indexForDirection = isBackwards ? index : (index + 1) || length;
  const sliceSize = isAround ? Math.round(MESSAGE_LIST_SLICE / 2) : MESSAGE_LIST_SLICE;
  const from = indexForDirection - sliceSize;
  const to = indexForDirection + sliceSize - 1;
  const newViewportIds = sourceIds.slice(Math.max(0, from), to + 1);

  let areAllLocal;
  switch (direction) {
    case LoadMoreDirection.Backwards:
      areAllLocal = from >= 0;
      break;
    case LoadMoreDirection.Forwards:
      areAllLocal = to <= length - 1;
      break;
    case LoadMoreDirection.Around:
    default:
      areAllLocal = newViewportIds.length === MESSAGE_LIST_SLICE;
      break;
  }

  return { newViewportIds, areAllLocal };
}

export function isBotViewportMessageRepairNeeded(message: ApiMessage) {
  return hasMessageText(message) && !message.content.text;
}

function selectBotLocalHistoryIds(global: GlobalState, chatId: string, threadId: ThreadId) {
  const listedIds = selectListedIds(global, chatId, threadId) || [];
  const messagesById = selectChatMessages(global, chatId);
  if (!messagesById) {
    return listedIds;
  }

  const messageIds: number[] = [];
  for (const message of Object.values(messagesById)) {
    if (selectThreadIdFromMessage(global, message) === threadId) {
      messageIds.push(message.id);
    }
  }

  if (!messageIds.length) {
    return listedIds;
  }

  return orderHistoryIds(unique(listedIds.length ? [...listedIds, ...messageIds] : messageIds));
}

export function updateBotLocalViewport(
  global: GlobalState,
  chatId: string,
  threadId: ThreadId,
  direction: LoadMoreDirection,
  tabId: number,
) {
  const historyIds = selectBotLocalHistoryIds(global, chatId, threadId);
  if (!historyIds.length) {
    return {
      global,
      historyIds,
      areAllLocal: false,
    };
  }

  let nextGlobal = updateListedIds(global, chatId, threadId, historyIds);
  const viewportIds = selectViewportIds(nextGlobal, chatId, threadId, tabId);
  const offsetId = direction === LoadMoreDirection.Around
    ? selectFocusedMessageId(nextGlobal, chatId, tabId) || selectRealLastReadId(nextGlobal, chatId, threadId)
    : direction === LoadMoreDirection.Backwards ? viewportIds?.[0] : viewportIds?.[viewportIds.length - 1];
  const { newViewportIds, areAllLocal } = getViewportSlice(historyIds, offsetId, direction);
  nextGlobal = safeReplaceViewportIds(nextGlobal, chatId, threadId, newViewportIds, tabId);

  return {
    global: nextGlobal,
    historyIds,
    offsetId,
    areAllLocal,
    viewportIds: newViewportIds,
  };
}

function shouldLoadBotKnownHistory(
  threadId: ThreadId,
  result: { areAllLocal: boolean },
) {
  return !result.areAllLocal && threadId === MAIN_THREAD_ID;
}

function filterBotKnownHistoryMessages(
  global: GlobalState,
  chatId: string,
  threadId: ThreadId,
  messages: ApiMessage[],
) {
  return messages.filter((message) => {
    return message.chatId === chatId && selectThreadIdFromMessage(global, message) === threadId;
  });
}

function getBotKnownHistoryAnchorId(
  global: GlobalState,
  chatId: string,
  direction: LoadMoreDirection,
  offsetId: number | undefined,
  historyIds: number[],
) {
  if (offsetId) return offsetId;
  if (direction === LoadMoreDirection.Backwards) return historyIds[0];
  if (direction === LoadMoreDirection.Forwards) return historyIds[historyIds.length - 1];
  return selectChatLastMessageId(global, chatId) || historyIds[historyIds.length - 1];
}

function getBotKnownHistoryRequestDirection(direction: LoadMoreDirection) {
  return direction === LoadMoreDirection.Forwards ? LoadMoreDirection.Forwards : LoadMoreDirection.Backwards;
}

function buildBotKnownHistoryMessageIds(anchorId: number, direction: LoadMoreDirection) {
  const isForwards = direction === LoadMoreDirection.Forwards;
  const messageIds: number[] = [];
  for (let i = 1; i <= BOT_KNOWN_HISTORY_PAGE_SIZE; i++) {
    const id = isForwards ? anchorId + i : anchorId - i;
    if (id <= 0) break;
    messageIds.push(id);
  }
  return messageIds;
}

function getNextBotKnownHistoryAnchorId(messageIds: number[]) {
  return messageIds[messageIds.length - 1];
}

function getBotKnownHistoryRequestKey(chatId: string, direction: LoadMoreDirection, anchorId: number) {
  return `${chatId}-${direction}-${anchorId}`;
}

function getMessageRequestKey(chatId: string, messageId: number) {
  return getMessageKey(chatId, messageId);
}

async function fetchMissingBotKnownHistoryAnchorMessage(
  chat: ApiChat,
  threadId: ThreadId,
  anchorId: number,
) {
  if (isLocalMessageId(anchorId)) return [];

  const chatId = chat.id;
  let currentGlobal = getGlobal();
  if (selectChatMessage(currentGlobal, chatId, anchorId)) return [];

  const requestKey = getMessageRequestKey(chatId, anchorId);
  if (messageRequestKeys.has(requestKey)) return [];

  messageRequestKeys.add(requestKey);
  const messages = await callApi('fetchMessagesById', { chat, messageIds: [anchorId] }).catch(() => undefined);
  messageRequestKeys.delete(requestKey);
  if (!messages?.length) return [];

  currentGlobal = getGlobal();
  return filterBotKnownHistoryMessages(currentGlobal, chatId, threadId, messages);
}

async function fetchBotKnownHistoryMessages(
  chat: ApiChat,
  threadId: ThreadId,
  direction: LoadMoreDirection,
  initialAnchorId: number,
) {
  const messages: ApiMessage[] = [];
  const chatId = chat.id;
  const historyDirection = getBotKnownHistoryRequestDirection(direction);
  let anchorId: number | undefined = initialAnchorId;

  for (let i = 0; i < BOT_KNOWN_HISTORY_MAX_PAGE_REQUESTS; i++) {
    if (!anchorId) break;

    const messageIds = buildBotKnownHistoryMessageIds(anchorId, historyDirection);
    if (!messageIds.length) break;

    const requestKey = getBotKnownHistoryRequestKey(chatId, historyDirection, anchorId);
    if (botKnownHistoryRequestKeys.has(requestKey)) break;

    if (botKnownHistoryEmptyPageKeys.has(requestKey)) {
      anchorId = getNextBotKnownHistoryAnchorId(messageIds);
      continue;
    }

    botKnownHistoryRequestKeys.add(requestKey);
    const pageMessages = await callApi('fetchMessagesById', { chat, messageIds }).catch(() => undefined);
    botKnownHistoryRequestKeys.delete(requestKey);
    if (!pageMessages) break;

    let currentGlobal = getGlobal();
    const pageMessagesForThread = filterBotKnownHistoryMessages(currentGlobal, chatId, threadId, pageMessages);
    if (!pageMessagesForThread.length) {
      botKnownHistoryEmptyPageKeys.add(requestKey);
      anchorId = getNextBotKnownHistoryAnchorId(messageIds);
      continue;
    }

    messages.push(...pageMessagesForThread);
    if (messages.length >= BOT_KNOWN_HISTORY_TARGET_SIZE) break;
    anchorId = getNextBotKnownHistoryAnchorId(messageIds);
  }

  return messages;
}

async function loadBotKnownHistoryViewportMessages(
  chat: ApiChat,
  threadId: ThreadId,
  direction: LoadMoreDirection,
  offsetId: number | undefined,
  tabId: number,
  onLoaded?: NoneToVoidFunction,
) {
  let global = getGlobal();
  const chatId = chat.id;
  const historyIds = selectBotLocalHistoryIds(global, chatId, threadId);
  const anchorId = getBotKnownHistoryAnchorId(global, chatId, direction, offsetId, historyIds);
  if (!anchorId || isLocalMessageId(anchorId)) {
    onLoaded?.();
    return;
  }

  const anchorMessages = await fetchMissingBotKnownHistoryAnchorMessage(chat, threadId, anchorId);
  const historyMessages = await fetchBotKnownHistoryMessages(chat, threadId, direction, anchorId);
  const messages = anchorMessages.length ? [...anchorMessages, ...historyMessages] : historyMessages;
  if (!messages.length) {
    onLoaded?.();
    return;
  }

  global = getGlobal();
  const ids = messages.map(({ id }) => id);
  global = addChatMessagesById(global, chatId, buildCollectionByKey(messages, 'id'));
  global = updateListedIds(global, chatId, threadId, ids);

  const listedIds = selectListedIds(global, chatId, threadId);
  if (listedIds?.length) {
    const { newViewportIds } = getViewportSlice(listedIds, offsetId, direction);
    global = safeReplaceViewportIds(global, chatId, threadId, newViewportIds, tabId);
  }

  setGlobal(global);
  onLoaded?.();
}

export async function repairBotMessages(
  chat: ApiChat,
  threadId: ThreadId,
  candidateIds: number[],
  tabId?: number,
) {
  const chatId = chat.id;
  let global = getGlobal();
  const messageIds = candidateIds.filter((id) => {
    if (isLocalMessageId(id)) return false;
    const message = selectChatMessage(global, chatId, id);
    if (!message || !isBotViewportMessageRepairNeeded(message)) return false;
    const requestKey = getMessageRequestKey(chatId, id);
    return !messageRequestKeys.has(requestKey) && !botViewportRepairAttemptKeys.has(requestKey);
  });

  if (!messageIds.length) return;

  const requestKeys = messageIds.map((id) => getMessageRequestKey(chatId, id));
  requestKeys.forEach((requestKey) => {
    messageRequestKeys.add(requestKey);
    botViewportRepairAttemptKeys.add(requestKey);
  });

  const result = await callApi('fetchMessagesByIdWithPeers', { chat, messageIds }).catch(() => undefined);
  requestKeys.forEach((requestKey) => messageRequestKeys.delete(requestKey));
  if (!result?.messages.length) return;

  global = getGlobal();
  const messages = filterBotKnownHistoryMessages(global, chatId, threadId, result.messages);
  if (!messages.length) return;

  global = addChatMessagesById(global, chatId, buildCollectionByKey(messages, 'id'));
  global = updateUsers(global, buildCollectionByKey(result.users, 'id'));
  global = updateChats(global, buildCollectionByKey(result.chats, 'id'));
  global = addUserStatuses(global, result.userStatusesById);
  if (tabId !== undefined) {
    global = safeReplaceViewportIds(global, chatId, threadId, candidateIds, tabId);
  }
  setGlobal(global);
}

export function handleBotLoadViewportMessages(
  global: GlobalState,
  chat: ApiChat,
  chatId: string,
  threadId: ThreadId,
  direction: LoadMoreDirection,
  isBudgetPreload: boolean,
  shouldForceRender: boolean,
  tabId: number,
  onLoaded?: NoneToVoidFunction,
) {
  const result = updateBotLocalViewport(global, chatId, threadId, direction, tabId);
  global = result.global;

  const { viewportIds: botViewportIds } = result;
  if (botViewportIds?.length) {
    let needsRepair = false;
    for (const id of botViewportIds) {
      const message = selectChatMessage(global, chatId, id);
      if (message && isBotViewportMessageRepairNeeded(message)) {
        needsRepair = true;
        break;
      }
    }
    if (needsRepair) {
      void repairBotMessages(chat, threadId, botViewportIds, tabId);
    }
  }

  if (!isBudgetPreload && shouldLoadBotKnownHistory(threadId, result)) {
    void loadBotKnownHistoryViewportMessages(chat, threadId, direction, result.offsetId, tabId, onLoaded);
  } else {
    onLoaded?.();
  }

  setGlobal(global, { forceOnHeavyAnimation: shouldForceRender });
}

function selectBotActiveChatIds(global: GlobalState) {
  const chatMeta: { chatId: string; date: number }[] = [];

  for (const chatId of Object.keys(global.messages.byChatId)) {
    const messages = selectChatMessages(global, chatId);
    if (!messages) continue;

    const chat = selectChat(global, chatId);
    const user = selectUser(global, chatId);
    if (!chat && !user) continue;

    let latestDate = 0;
    for (const message of Object.values(messages)) {
      if (message.date > latestDate) {
        latestDate = message.date;
      }
    }

    if (latestDate > 0) {
      chatMeta.push({ chatId, date: latestDate });
    }
  }

  chatMeta.sort((a, b) => b.date - a.date);
  return chatMeta.map(({ chatId }) => chatId);
}

function buildBotPrivateChats(global: GlobalState, chatIds: string[]) {
  const privateChats: GlobalState['chats']['byId'] = {};

  for (const chatId of chatIds) {
    const existingChat = selectChat(global, chatId);
    if (existingChat) {
      privateChats[chatId] = existingChat;
      continue;
    }

    const user = selectUser(global, chatId);
    const title = getUserFullName(user);
    if (!user || !title) continue;

    privateChats[chatId] = {
      id: user.id,
      type: 'chatTypePrivate',
      title,
      isMin: user.isMin,
      accessHash: user.accessHash,
      isVerified: user.isVerified,
      usernames: user.usernames,
      hasUsername: user.hasUsername,
      hasVideoAvatar: user.hasVideoAvatar,
      avatarPhotoId: user.avatarPhotoId,
      isSupport: user.isSupport,
      fakeType: user.fakeType,
      color: user.color,
      profileColor: user.profileColor,
      emojiStatus: user.emojiStatus,
      isForum: user.isBotForum,
      isBotForum: user.isBotForum,
      botVerificationIconId: user.botVerificationIconId,
      paidMessagesStars: user.paidMessagesStars,
    };
  }

  return privateChats;
}

function buildBotLastMessageIds(global: GlobalState, chatIds: string[]) {
  const lastMessageByChatId: Record<string, number> = {};

  for (const chatId of chatIds) {
    const messages = selectChatMessages(global, chatId);
    if (!messages) continue;

    let latestId: number | undefined;
    let latestDate = 0;
    for (const message of Object.values(messages)) {
      if (message.date >= latestDate) {
        latestDate = message.date;
        latestId = message.id;
      }
    }

    if (latestId) {
      lastMessageByChatId[chatId] = latestId;
    }
  }

  return lastMessageByChatId;
}

export function finishBotChatList(global: GlobalState) {
  const activeIds = unique([
    ...(global.chats.listIds.active || []),
    ...selectBotActiveChatIds(global),
  ]);
  const privateChats = buildBotPrivateChats(global, activeIds);
  const lastMessageByChatId = buildBotLastMessageIds(global, activeIds);

  return {
    ...global,
    chats: {
      ...global.chats,
      byId: {
        ...global.chats.byId,
        ...privateChats,
      },
      listIds: {
        ...global.chats.listIds,
        active: activeIds,
        archived: global.chats.listIds.archived || [],
        saved: global.chats.listIds.saved || [],
      },
      isFullyLoaded: {
        ...global.chats.isFullyLoaded,
        active: true,
        archived: true,
        saved: global.chats.isFullyLoaded.saved ?? true,
      },
      lastMessageIds: {
        ...global.chats.lastMessageIds,
        all: {
          ...global.chats.lastMessageIds.all,
          ...lastMessageByChatId,
        },
      },
    },
  };
}
